import { ContainerQuery } from '@/components/container-query/ContainerQuery';
import { useGenerateBreadcrumbList } from '@/hooks/useGenerateBreadcrumbList';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import type { DataSchemaServerType } from '@/services/data-schema/dataSchema';
import { isContainerQueryFallback, isOldSafari } from '@/shared/util/ssr';
import { JsonSchemaViewerCopyableName } from '@/ui-kit/components/JsonSchemaViewer/components/PropertyNameRender';
import { JsonSchemaViewer } from '@/ui-kit/components/JsonSchemaViewer/JsonSchemaViewer';
import { ParameterDisplayMode } from '@tarslib/types/lib/projectSetting';
import { AppJsonSchema2JsonSchemaStandard } from '@tarslib/utils/lib/json-schema/AppJsonSchema2JsonSchemaStandard';
import type { JSONSchema } from '@tarslib/utils/lib/json-schema/type';
import { usePersistFn } from 'ahooks';
import type { JSONSchema7 } from 'json-schema';
import React, { useMemo } from 'react';
import { BottomBanner } from '../bottom-banner/BottomBanner';
import { DocsBreadcrumb } from '../docs-breadcrumb/DocsBreadcrumb';
import { Footer } from '../footer/Footer';
import { JsonSchemaEnum } from '../json-schema-enum/JsonSchemaEnum';
import { MarkdownViewer } from '../markdown-viewer/MarkdownViewer';
import { ResourceHeader } from '../resource-header/ResourceHeader';
import { SpecialFileMenu } from '../special-file-menu/SpecialFileMenu';
import { DataSchemaExample } from './components/data-schema-example/DataSchemaExample';

export type DataSchemaContentProps = {
  className?: string;
  value:
    | (JSONSchema7 & {
        name: string | undefined;
      })
    | null;
  extraData: {
    dataSchemaDefinitions: DataSchemaServerType;
    example: string;
    description?: string;
    displayName?: string;
  };
};

export function DataSchemaContent(props: DataSchemaContentProps) {
  const { value, extraData } = props;
  const breadcrumbList = useGenerateBreadcrumbList().filter((x) => x.name);
  const locale = useDocsDataModelStore((x) => x.docsBaseConfig.language);
  const appNameLower = useEnvConfigModelStore((x) => x.APP_NAME_LOWER);
  const [containerSize, setContainerSize] = React.useState(0);
  const [isOldSafariRender, setIsOldSafariRender] = React.useState(false);
  const showDataSchemasExample = useDocsDataModelStore(
    (x) => x.docsBaseConfig.showDataSchemasExample,
  );

  const schema = useMemo(() => {
    if (value) {
      return AppJsonSchema2JsonSchemaStandard(value, [], {
        enableXAppEnum: true,
        enableXAppMock: true,
      });
    }
  }, [value]);

  const dataSchemaNameWithDisplayName = useMemo(() => {
    if (extraData?.displayName && value?.name) {
      return `${value.name}(${extraData.displayName})`;
    }
    return value?.name || '';
  }, [value, extraData]);

  const descriptionRender = usePersistFn((description: string) => (
    <MarkdownViewer value={description} enableHeaderPlugin={false} inDocView={false} />
  ));

  const expandedInfos = React.useMemo(() => {
    return {
      typesRightRender: () => <></>,
    };
  }, []);

  const valueRenderMap = React.useMemo(() => {
    return {
      enum: (name: string, enumDescriptions?: JSONSchema['x-app-enum']) => {
        return <JsonSchemaEnum name={name} enumDescriptions={enumDescriptions} />;
      },
    };
  }, []);

  React.useEffect(() => {
    if (isOldSafari) {
      setIsOldSafariRender(true);
    }
  }, []);

  return (
    <>
      <ContainerQuery
        containerSizes={[850, 1000, 1440]}
        className="flex @container/http-api-content os:pt-5"
        isContainerQueryFallback={isOldSafari || isContainerQueryFallback}
        onContainerSizeChange={(size) => {
          if (!isOldSafari) {
            return;
          }
          setContainerSize(size);
        }}
      >
        <div className="w-0 flex-1 @[850px]/http-api-content:pr-[440px]">
          <DocsBreadcrumb
            className="mb-2 hidden os:flex"
            value={breadcrumbList}
            rightNode={!!breadcrumbList.length && <SpecialFileMenu />}
          />

          <ResourceHeader
            name={dataSchemaNameWithDisplayName}
            className="mb-6"
            suffix={!breadcrumbList.length && <SpecialFileMenu className="max-os:hidden" />}
            suffixClassName="!h-auto"
          />

          <MarkdownViewer
            value={extraData?.description || ''}
            inDocView
            enableHeaderPlugin
            className="mb-6"
          />

          {schema && (
            <JsonSchemaViewer
              className="mb-6"
              schema={schema}
              appNameLower={appNameLower}
              locale={locale}
              descriptionRender={descriptionRender}
              disableCrumbs
              expandedInfos={expandedInfos}
              isStateRightAligned={false}
              isMergeAllOf
              viewMode="standalone"
              valueRenderMap={valueRenderMap}
              displayMode={ParameterDisplayMode.MODERN as any}
              propertyNameRender={JsonSchemaViewerCopyableName}
            />
          )}

          {showDataSchemasExample && (
            <DataSchemaExample
              code={extraData.example}
              className="mb-8 @[850px]/http-api-content:mb-0 @[850px]/http-api-content:group-[.old-safari]/root:hidden"
            />
          )}

          <BottomBanner className="mb-8" />

          <Footer />
        </div>
      </ContainerQuery>
      {showDataSchemasExample && isOldSafariRender && !!containerSize && (
        <div className="pointer-events-none fixed left-0 top-0 mx-[max(calc((100vw-1560px)/2),0px)] h-full w-full max-w-[1560px] @container/http-api-content">
          <DataSchemaExample
            className="mb-0 @[850px]/http-api-content:group-[.old-safari]/root:mr-0 @[850px]/http-api-content:group-[.old-safari]/root:pl-0 @[850px]/http-api-content:group-[.old-safari]/root:pr-5"
            code={extraData.example}
          />
        </div>
      )}
    </>
  );
}
